import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, Sparkles, X } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { activeSection, navSections, sectionActive, itemActive, itemTestId, loadExpanded, saveExpanded } from "@/lib/nav";
import { useInboxSummary } from "@/lib/inbox";

// ONE interaction pattern for every parent: the label is a real link to the section's landing page; the chevron is a
// separate button that only expands/collapses the children (aria-expanded). Same on desktop (dropdown) and mobile (sheet).

const Badge = ({ b }) => (b ? <span className="text-[8px] font-bold bg-primary text-primary-foreground rounded px-1 py-0.5 leading-none">{b}</span> : null);
const Count = ({ n, testid }) => (typeof n === "number" ? <span data-testid={testid} className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-bold leading-none tabular-nums">{n}</span> : null);
const countFor = (s, summary) => (s.countKey === "inbox" && summary ? summary.count : undefined);

const DesktopSection = ({ s, active, pathname, search, count }) => {
  const [open, setOpen] = useState(false);
  const lit = sectionActive(s, active, pathname, search);
  const labelCls = cn("font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded-sm px-0.5",
    s.emphasis && "font-semibold", lit ? "text-primary" : "text-muted-foreground hover:text-foreground");
  if (!s.items.length) {
    return <Link to={s.to} data-testid={s.testid} data-active={lit ? "true" : "false"} aria-current={lit ? "page" : undefined} className={labelCls}>{s.label}</Link>;
  }
  const menuId = `${s.testid}-menu`;
  return (
    <div data-testid={`${s.testid}-group`} data-active={lit ? "true" : "false"} className="inline-flex items-center gap-0.5">
      <Link to={s.to} data-testid={s.testid} data-active={lit ? "true" : "false"} aria-current={lit ? "page" : undefined} className={cn(labelCls, "inline-flex items-center")}>{s.label}<Count n={count} testid={`${s.testid}-count`} /></Link>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button type="button" data-testid={`${s.testid}-toggle`} aria-label={`${open ? "Collapse" : "Expand"} ${s.label} menu`} aria-expanded={open} aria-controls={menuId}
            onPointerDown={(e) => e.preventDefault()} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
            className={cn("h-8 w-7 inline-flex items-center justify-center rounded-md transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
              lit ? "text-primary" : "text-muted-foreground hover:text-foreground hover:bg-white/5")}>
            <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", open && "rotate-180")} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent id={menuId} align="start" className="w-48 bg-popover border-border" data-testid={menuId} onEscapeKeyDown={() => setOpen(false)} onPointerDownOutside={() => setOpen(false)}>
          {s.items.map((n) => {
            const on = itemActive(n, active, pathname, search);
            return (
              <DropdownMenuItem key={n.key || n.to} asChild data-active={on ? "true" : "false"} data-testid={`${s.testid}-${itemTestId(n)}`} className={cn("flex items-center justify-between cursor-pointer", on && "text-primary font-medium")}>
                <Link to={n.to} aria-current={on ? "page" : undefined}>{n.l}<Badge b={n.badge} /></Link>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const SiteNav = ({ user }) => {
  const location = useLocation();
  const active = activeSection(location.pathname, location.search);
  const sections = useMemo(() => navSections(user), [user]);
  const summary = useInboxSummary(!!user?.is_admin);
  return (
    <nav data-testid="main-nav" aria-label="Main" className="hidden lg:flex items-center gap-5 xl:gap-7 text-sm whitespace-nowrap">
      {sections.map((s) => <DesktopSection key={s.key} s={s} active={active} pathname={location.pathname} search={location.search} count={countFor(s, summary)} />)}
    </nav>
  );
};

export const AiSearchButton = ({ onClick }) => (
  <button data-testid="ai-search-open" onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 text-primary px-3 h-9 text-sm font-medium hover:bg-primary/10 transition-colors whitespace-nowrap">
    <Sparkles className="w-4 h-4" /> <span className="hidden sm:inline">AI Search</span>
  </button>
);

const MobileSection = ({ s, active, pathname, search, count, expanded, onToggle, onNavigate }) => {
  const lit = sectionActive(s, active, pathname, search);
  const panelId = `m${s.testid}-panel`;
  return (
    <li data-testid={`m${s.testid}-group`} data-active={lit ? "true" : "false"} className="border-b border-border/60 last:border-0">
      <div className="flex items-center">
        <Link to={s.to} data-testid={`m${s.testid}`} data-active={lit ? "true" : "false"} aria-current={lit ? "page" : undefined} onClick={onNavigate}
          className={cn("flex-1 min-h-[48px] flex items-center px-4 text-base font-medium outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded-md",
            s.emphasis && "font-semibold", lit ? "text-primary" : "text-foreground")}>{s.label}<Count n={count} testid={`m${s.testid}-count`} /></Link>
        {s.items.length > 0 && (
          <button type="button" data-testid={`m${s.testid}-toggle`} aria-expanded={expanded} aria-controls={panelId} aria-label={`${expanded ? "Collapse" : "Expand"} ${s.label}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(s.key); }}
            className={cn("h-12 w-12 shrink-0 inline-flex items-center justify-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary/60 active:bg-white/10", lit ? "text-primary" : "text-muted-foreground")}>
            <ChevronDown className={cn("w-5 h-5 transition-transform", expanded && "rotate-180")} />
          </button>
        )}
      </div>
      {s.items.length > 0 && expanded && (
        <ul id={panelId} data-testid={panelId} className="pb-2">
          {s.items.map((n) => {
            const on = itemActive(n, active, pathname, search);
            return (
              <li key={n.key || n.to}>
                <Link to={n.to} data-testid={`m${s.testid}-${itemTestId(n)}`} data-active={on ? "true" : "false"} aria-current={on ? "page" : undefined} onClick={onNavigate}
                  className={cn("min-h-[44px] flex items-center justify-between pl-8 pr-4 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded-md",
                    on ? "text-primary font-medium" : "text-muted-foreground")}>
                  <span>{n.l}</span><Badge b={n.badge} />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

// Mobile / tablet hamburger — same parent+chevron pattern in a slide-in sheet; expanded sections are remembered.
export const MobileNavMenu = ({ user }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(loadExpanded);
  const active = activeSection(location.pathname, location.search);
  const sections = useMemo(() => navSections(user, { mobile: true }), [user]);
  const summary = useInboxSummary(!!user?.is_admin);
  const activeKey = sections.find((s) => sectionActive(s, active, location.pathname, location.search))?.key;
  useEffect(() => { if (activeKey) setExpanded((e) => (e.includes(activeKey) ? e : [...e, activeKey])); }, [activeKey]);
  const toggle = (key) => setExpanded((e) => { const n = e.includes(key) ? e.filter((k) => k !== key) : [...e, key]; saveExpanded(n); return n; });
  return (
    <>
      <button type="button" data-testid="nav-mobile-menu" aria-label="Menu" aria-expanded={open} aria-controls="mobile-nav-sheet" onClick={() => setOpen(true)}
        className="lg:hidden h-9 w-9 inline-flex items-center justify-center rounded-full border border-border hover:border-primary hover:text-primary transition-colors">
        <Menu className="w-4 h-4" />
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent id="mobile-nav-sheet" side="right" data-testid="mobile-nav-sheet" className="w-[86vw] max-w-xs p-0 bg-background border-border lg:hidden [&>button]:hidden">
          <div className="h-14 px-4 flex items-center justify-between border-b border-border">
            <SheetTitle className="font-heading text-lg text-primary">Menu</SheetTitle>
            <button type="button" data-testid="mobile-nav-close" aria-label="Close menu" onClick={() => setOpen(false)} className="h-10 w-10 inline-flex items-center justify-center rounded-full hover:bg-white/5"><X className="w-5 h-5" /></button>
          </div>
          <nav aria-label="Main (mobile)" className="overflow-y-auto max-h-[calc(100vh-56px)]">
            <ul>
              {sections.map((s) => (
                <MobileSection key={s.key} s={s} active={active} pathname={location.pathname} search={location.search} count={countFor(s, summary)} expanded={expanded.includes(s.key)} onToggle={toggle} onNavigate={() => setOpen(false)} />
              ))}
            </ul>
          </nav>
        </SheetContent>
      </Sheet>
    </>
  );
};
